import React, { useEffect, useState } from 'react';
import { Loader2, TrendingUp } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Graphique CA mensuel (12 mois glissants) — encaissé vs en attente.
 * Optimisé pour les rendez-vous fiduciaire et la lecture des tendances.
 */
export const RevenueChart = ({ onLogout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/billing/chart?months=12`, { credentials: 'include' });
        if (res.status === 401) { onLogout?.(); return; }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Erreur chargement graphique');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [onLogout]);

  if (loading) {
    return (
      <div className="bg-zinc-900/40 border border-white/10 rounded-2xl p-6 flex items-center justify-center h-72">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-zinc-900/40 border border-white/10 rounded-2xl p-6 text-center text-zinc-400 h-72 flex flex-col items-center justify-center gap-2" data-testid="billing-revenue-chart-error">
        <TrendingUp className="w-8 h-8 text-zinc-600" />
        <p className="text-sm">Impossible de charger le graphique</p>
        <p className="text-xs text-zinc-500">{error}</p>
      </div>
    );
  }

  if (!data || !data.series?.length) return null;

  const hasAnyData = data.totals.revenue_paid > 0 || data.totals.revenue_pending > 0;

  return (
    <div className="bg-zinc-900/40 border border-white/10 rounded-2xl p-4 sm:p-5" data-testid="billing-revenue-chart">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-white font-semibold inline-flex items-center gap-2 text-sm">
          <TrendingUp className="w-4 h-4 text-primary" /> CA mensuel — 12 mois glissants
        </h3>
        <div className="text-zinc-400 text-xs">
          Total : <span className="text-white font-bold">{data.totals.revenue_paid.toFixed(0)} CHF</span>
          {data.totals.revenue_pending > 0 && (
            <span className="text-zinc-500 ml-2">+ {data.totals.revenue_pending.toFixed(0)} CHF en attente</span>
          )}
        </div>
      </div>

      {!hasAnyData ? (
        <div className="h-56 flex items-center justify-center text-zinc-500 text-sm">
          Aucune donnée à afficher pour cette période.
        </div>
      ) : (
        <div className="h-72" data-testid="billing-revenue-chart-canvas">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.series} margin={{ top: 5, right: 5, bottom: 0, left: -15 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="month_label" tick={{ fill: '#a1a1aa', fontSize: 11 }} axisLine={{ stroke: '#3f3f46' }} />
              <YAxis tick={{ fill: '#a1a1aa', fontSize: 11 }} axisLine={{ stroke: '#3f3f46' }}
                tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} />
              <Tooltip
                cursor={{ fill: 'rgba(233,30,99,0.05)' }}
                contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 12, color: '#fff' }}
                labelStyle={{ color: '#fafafa', fontWeight: 600, marginBottom: 4 }}
                formatter={(value, name) => [`${value.toFixed(2)} CHF`, name === 'revenue_paid' ? 'Encaissé' : 'En attente']}
              />
              <Legend
                wrapperStyle={{ color: '#a1a1aa', fontSize: 11, paddingTop: 8 }}
                formatter={(v) => v === 'revenue_paid' ? 'Encaissé' : 'En attente'}
              />
              <Bar dataKey="revenue_paid" stackId="rev" fill="#10B981" radius={[0, 0, 4, 4]} />
              <Bar dataKey="revenue_pending" stackId="rev" fill="#FBBF24" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};
